"use client";

import { createContext, useContext, useEffect, useState } from "react";

import en from "@/i18n/en.json";
import es from "@/i18n/es.json";

const LANGUAGE = {
  EN: "en",
  ES: "es",
} as const;

type Language = (typeof LANGUAGE)[keyof typeof LANGUAGE];

const DEFAULT_LANGUAGE: Language = LANGUAGE.ES;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = "cos-language";

const translations: Record<Language, Record<string, string>> = { en, es };

export function LanguageProvider({
  children,
  initialLanguage,
}: {
  children: React.ReactNode;
  initialLanguage?: Language;
}) {
  // Use a consistent default for BOTH server and initial client render
  // to prevent hydration mismatch. Browser detection happens after mount.
  const [language, setLanguageState] = useState<Language>(
    initialLanguage ?? DEFAULT_LANGUAGE,
  );

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  };

  const t = (key: string): string => {
    const value = translations[language][key];
    if (value !== undefined) return value;

    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[i18n] Missing translation key: "${key}" for language: "${language}"`,
      );
    }
    return key;
  };

  // Detect actual language preference AFTER hydration to avoid mismatch.
  // Server and client initial render both use DEFAULT_LANGUAGE ("es").
  // Only after mount do we check localStorage / navigator.language.
  useEffect(() => {
    if (initialLanguage) return; // explicit prop takes precedence — no detection

    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored === LANGUAGE.EN || stored === LANGUAGE.ES) {
      setLanguageState(stored);
      document.documentElement.lang = stored;
      return;
    }

    const browserLang = (navigator.language || "").startsWith("es")
      ? LANGUAGE.ES
      : LANGUAGE.EN;
    setLanguageState(browserLang);
    document.documentElement.lang = browserLang;
  }, [initialLanguage]);

  // Keep html lang attribute in sync when language changes after mount
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
